import { ArrowForwardOutlined } from '@mui/icons-material';
import { Sync as SyncIcon } from '@mui/icons-material';
import { Button, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GcfComponentSection } from 'src/components/gcfComponentSection/GcfComponentSection';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { useResizeObserver } from 'src/hooks';
import { ComponentTypes } from 'src/interfaces';
import { lang } from 'src/utils';
import { getSectionPreviewData } from 'src/utils/gcf-helper';

import {
  ComponentContainer,
  ComponentHeaderSection,
  ComponentHeaderTitle,
  ComponentName,
  ComponentPreviewContainer,
  ComponentPreviewWrapper,
  ExistingComponentName,
  LoadingContainer,
  MediaContainer,
  ReplaceComponentContainer,
} from '../BulkReplaceComponentPage.styled';
import { useComponentData } from '../hooks/useComponentData';

interface ComponentReplaceSectionProps {
  onChangeComponent?: () => void;
}

export const ComponentReplaceSection = ({
  onChangeComponent,
}: ComponentReplaceSectionProps) => {
  const { containerRef, ...containerSize } = useResizeObserver();
  const { containerRef: newContainerRef, ...newContainerSize } =
    useResizeObserver();

  const navigate = useNavigate();

  const {
    originalComponent: componentDetails,
    replacementComponent: replaceComponentDetails,
    isLoadingOriginalComponent: isLoadingComponent,
    isLoadingReplacementComponent: isLoadingReplaceComponent,
  } = useComponentData();

  const handleChangeComponent = () => {
    onChangeComponent?.();
    navigate(PATHS.COMPONENT_REPLACE(componentDetails?.id));
  };

  return (
    <ComponentContainer container>
      <ComponentHeaderSection item xs={12}>
        <Grid container alignItems="center">
          <Grid item xs={5}>
            <ComponentHeaderTitle variant="subtitle1">
              {lang.bulkReplaceComponentPage.existingComponent}
            </ComponentHeaderTitle>
          </Grid>
          <Grid item xs={1}></Grid>
          <Grid item xs={5}>
            <ComponentHeaderTitle variant="subtitle1">
              {lang.bulkReplaceComponentPage.newComponent}
            </ComponentHeaderTitle>
          </Grid>
        </Grid>
      </ComponentHeaderSection>

      <Grid container item xs={12} alignItems="center" sx={{ padding: '16px' }}>
        <Grid item xs={5}>
          {componentDetails && !isLoadingComponent && (
            <ComponentPreviewContainer>
              <ComponentPreviewWrapper>
                <MediaContainer ref={containerRef}>
                  <GcfComponentSection
                    assemblyComponents={[
                      {
                        component: {
                          type: componentDetails?.component_type?.name,
                          config: componentDetails?.config,
                          name: componentDetails?.name,
                          width: componentDetails?.width,
                          height: componentDetails?.height,
                          theme: componentDetails?.theme?.name || 'default',
                        },
                      },
                    ]}
                    containerSize={containerSize}
                    height={componentDetails?.height || 0}
                    width={componentDetails?.width || 0}
                    previewData={getSectionPreviewData(
                      componentDetails?.component_type?.name as ComponentTypes,
                    )}
                    containerStyle={{
                      borderRadius: '8px',
                    }}
                  />
                </MediaContainer>
              </ComponentPreviewWrapper>

              <CustomTooltip
                width={500}
                placement="right"
                overflowLineNumber={1}
                title={componentDetails?.name}>
                <ExistingComponentName variant="body2">
                  {componentDetails?.name}
                </ExistingComponentName>
              </CustomTooltip>
            </ComponentPreviewContainer>
          )}
        </Grid>

        <Grid item xs={1}>
          <ArrowForwardOutlined
            color="primary"
            sx={{
              border: '2px solid',
              borderColor: 'brand.ash',
              borderRadius: '50px',
              fontSize: '42px',
            }}
          />
        </Grid>

        <Grid item xs={'auto'}>
          {replaceComponentDetails && !isLoadingReplaceComponent ? (
            <ReplaceComponentContainer>
              <ComponentPreviewWrapper>
                <MediaContainer ref={newContainerRef}>
                  <GcfComponentSection
                    assemblyComponents={[
                      {
                        component: {
                          type: replaceComponentDetails?.component_type?.name,
                          config: replaceComponentDetails?.config,
                          name: replaceComponentDetails?.name,
                          width: replaceComponentDetails?.width,
                          height: replaceComponentDetails?.height,
                          theme:
                            replaceComponentDetails?.theme?.name || 'default',
                        },
                      },
                    ]}
                    containerSize={newContainerSize}
                    height={replaceComponentDetails?.height || 0}
                    width={replaceComponentDetails?.width || 0}
                    previewData={getSectionPreviewData(
                      replaceComponentDetails?.component_type
                        ?.name as ComponentTypes,
                    )}
                    containerStyle={{
                      borderRadius: '8px',
                    }}
                  />
                </MediaContainer>
              </ComponentPreviewWrapper>
              <div>
                <CustomTooltip
                  width={500}
                  placement="right"
                  overflowLineNumber={1}
                  title={replaceComponentDetails?.name}>
                  <ComponentName variant="body2">
                    {replaceComponentDetails?.name}
                  </ComponentName>
                </CustomTooltip>

                <Button
                  variant="outlined"
                  onClick={handleChangeComponent}
                  startIcon={<SyncIcon />}>
                  {lang.changeComponent}
                </Button>
              </div>
            </ReplaceComponentContainer>
          ) : (
            <LoadingContainer>
              <Typography color="textSecondary">
                {lang.bulkReplaceComponentPage.loadingComponent}
              </Typography>
            </LoadingContainer>
          )}
        </Grid>
      </Grid>
    </ComponentContainer>
  );
};
